import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";

/**
 * Scopes:
 *   - "system": built-in registry shared across teams.
 *   - "team":   row in team_semantic_column, scoped to a team. Can be resolved or calculated.
 *   - "rfq":    row in rfq_semantic_column, scoped to a single RFQ. Always calculated; used
 *               for ad-hoc cross-vendor analyses that don't need to live in the team library.
 */
export type SemanticColumnScope = "system" | "team" | "rfq";

/**
 * A structured reference to a specific semantic column: which source it lives in plus the raw
 * key. Used everywhere a single value would otherwise need to encode both facts (e.g. formerly
 * "team:cfm"). The scope is always a first-class field.
 */
export type SemanticColumnRef = {
    scope: SemanticColumnScope;
    key: string;
};

export type SemanticColumnCategory =
    | "identification"
    | "quantity"
    | "pricing"
    | "terms"
    | "specifications"
    | "metadata";

export type SemanticColumnDataType = "text" | "number" | "currency" | "date";

export type SystemSemanticColumn = {
    key: string;
    name: string;
    category: SemanticColumnCategory;
    dataType: SemanticColumnDataType;
    description: string;
    llmDescription: string;
    aliases: string[];
};

/**
 * How a semantic column produces a value at quote time.
 *   - "resolved":   try-to-map at identification, fall back to extraction at derivation. The
 *                   default; this is how every existing system + team column behaves today.
 *   - "calculated": value is computed from `inputs` (other semantic columns) using a recipe
 *                   planned by the LLM and executed deterministically.
 */
export type ResolutionType = "resolved" | "calculated";

export type TeamSemanticColumn = {
    id: number;
    teamId: number;
    key: string;
    name: string;
    category: SemanticColumnCategory;
    dataType: SemanticColumnDataType;
    description: string;
    llmDescription: string;
    aliases: string[];
    resolutionType: ResolutionType;
    /** Calculated only. Free-form unit string the executed plan must arrive at. */
    targetUnit?: string | null;
    /** Calculated only. Inputs that feed the recipe (system or team refs). */
    inputs?: SemanticColumnRef[] | null;
    /** Calculated only. Free-text hints surfaced to the planner. */
    llmNotes?: string | null;
    /** Calculated only. Bumped on edits that affect plan generation. */
    recipeVersion?: number;
    createdAt: string;
    updatedAt: string;
};

export type TeamSystemColumnEnrichment = {
    id: number;
    teamId: number;
    /** References a system semantic column key; team-scope enrichments aren't supported. */
    semanticColumnKey: string;
    additionalAliases: string[];
    additionalLlmDescription: string | null;
    createdAt: string;
    updatedAt: string;
};

export type DedupMatch = {
    /** The existing semantic column the proposal duplicates, as a structured ref. */
    semanticColumn: SemanticColumnRef;
    reason: string;
};

export type DedupCheckResult = {
    systemMatch: DedupMatch | null;
    teamMatch: DedupMatch | null;
};

export type AvailableSemanticColumnOption = {
    scope: SemanticColumnScope;
    key: string;
    name: string;
    category: SemanticColumnCategory;
    dataType: SemanticColumnDataType;
    description: string;
};

export type QuoteView = {
    id: number;
    rfqId: number;
    name: string;
    semanticColumnScope: SemanticColumnScope;
    semanticColumnKey: string;
    createdAt: string;
    updatedAt: string;
};

/**
 * An RFQ-scoped calculated semantic column. Created ad-hoc on a single RFQ; can be promoted
 * to the team library if it proves broadly useful.
 */
export type RFQCalculatedColumn = {
    id: number;
    rfqId: number;
    key: string;
    name: string;
    category: SemanticColumnCategory;
    dataType: SemanticColumnDataType;
    description: string;
    targetUnit: string;
    inputs: SemanticColumnRef[];
    llmNotes: string | null;
    /** RFQ row ids this column is scoped to. Empty array (the default) means the column
     *  applies to every line item on the RFQ. When populated, the calculate pipeline only
     *  runs the recipe against vendor rows linked to one of these RFQ rows; out-of-scope
     *  rows render a clean "outside this column's row scope" unable. Buyers set this in
     *  the new-column / reprocess modals when a recipe only makes sense for some items. */
    applicableRfqRowIds: number[];
    recipeVersion: number;
    createdByUserId: number | null;
    createdAt: string;
    updatedAt: string;
};

async function req<T>(path: string, init?: RequestInit): Promise<T> {
    // Fastify rejects empty bodies when Content-Type is application/json (which getHeaders
    // sets), so fill in `{}` for any method that doesn't already have a body. Keeps callers
    // from having to remember to pass `body: JSON.stringify({})` on every DELETE.
    const method = (init?.method ?? "GET").toUpperCase();
    const body = init?.body ?? (method === "GET" || method === "HEAD" ? undefined : JSON.stringify({}));
    const res = await fetch(PURCHASER_API_BASE_URL + path, {
        ...init,
        body,
        headers: { ...getHeaders(), ...(init?.headers || {}) }
    });
    const resBody = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err: any = new Error((resBody && resBody.message) || res.statusText);
        err.status = res.status;
        err.body = resBody;
        throw err;
    }
    return resBody as T;
}

export default {
    getSystemSemanticColumns: () => req<{ systemColumns: SystemSemanticColumn[] }>("/semantic-columns/system"),

    getTeamSemanticColumns: () => req<{ teamColumns: TeamSemanticColumn[] }>("/semantic-columns/team"),

    checkTeamSemanticColumnDedup: (proposed: {
        name: string;
        description: string;
        llmDescription: string;
        aliases: string[];
    }) =>
        req<DedupCheckResult>("/semantic-columns/team/check-dedup", {
            method: "POST",
            body: JSON.stringify(proposed)
        }),

    createTeamSemanticColumn: (body: {
        /** Server derives this from `name` when omitted (slugify + auto-suffix on collision).
         *  Most callers should leave it absent. Sending it explicitly is supported for older
         *  clients and scripts that want a stable identifier. */
        key?: string;
        name: string;
        category: SemanticColumnCategory;
        dataType: SemanticColumnDataType;
        description: string;
        llmDescription: string;
        aliases: string[];
        /** Defaults to "resolved" server-side if omitted. */
        resolutionType?: ResolutionType;
        targetUnit?: string;
        inputs?: { scope: "system" | "team"; key: string }[];
        llmNotes?: string | null;
    }) =>
        req<{ teamColumn: TeamSemanticColumn; teamMatch: DedupMatch | null }>("/semantic-columns/team", {
            method: "POST",
            body: JSON.stringify(body)
        }),

    updateTeamSemanticColumn: (
        id: number,
        body: {
            name?: string;
            description?: string;
            llmDescription?: string;
            aliases?: string[];
            category?: SemanticColumnCategory;
            dataType?: SemanticColumnDataType;
            targetUnit?: string;
            inputs?: { scope: "system" | "team"; key: string }[];
            llmNotes?: string | null;
        }
    ) =>
        req<{ teamColumn: TeamSemanticColumn; recipeVersionBumped: boolean }>(`/semantic-columns/team/${id}`, {
            method: "PUT",
            body: JSON.stringify(body)
        }),

    deleteTeamSemanticColumn: (id: number) =>
        req<{ success: true }>(`/semantic-columns/team/${id}`, { method: "DELETE" }),

    getTeamSystemColumnEnrichments: () =>
        req<{ enrichments: TeamSystemColumnEnrichment[] }>("/semantic-columns/enrichments"),

    createTeamSystemColumnEnrichment: (body: {
        semanticColumnKey: string;
        additionalAliases: string[];
        additionalLlmDescription?: string | null;
    }) =>
        req<TeamSystemColumnEnrichment>("/semantic-columns/enrichments", {
            method: "POST",
            body: JSON.stringify(body)
        }),

    updateTeamSystemColumnEnrichment: (
        id: number,
        body: { additionalAliases?: string[]; additionalLlmDescription?: string | null }
    ) =>
        req<TeamSystemColumnEnrichment>(`/semantic-columns/enrichments/${id}`, {
            method: "PUT",
            body: JSON.stringify(body)
        }),

    deleteTeamSystemColumnEnrichment: (id: number) =>
        req<{ success: true }>(`/semantic-columns/enrichments/${id}`, { method: "DELETE" }),

    getAvailableSemanticColumnsForRFQ: (rfqUniquePublicId: string) =>
        req<{ options: AvailableSemanticColumnOption[] }>(
            `/v2/rfqs/${rfqUniquePublicId}/available-semantic-columns`
        ),

    listRFQQuoteViews: (rfqUniquePublicId: string) =>
        req<{ quoteViews: QuoteView[] }>(`/v2/rfqs/${rfqUniquePublicId}/quote-views`),

    createRFQQuoteView: (
        rfqUniquePublicId: string,
        body: { name: string; semanticColumn: SemanticColumnRef }
    ) =>
        req<QuoteView>(`/v2/rfqs/${rfqUniquePublicId}/quote-views`, {
            method: "POST",
            body: JSON.stringify(body)
        }),

    deleteRFQQuoteView: (rfqUniquePublicId: string, id: number) =>
        req<{ success: true }>(`/v2/rfqs/${rfqUniquePublicId}/quote-views/${id}`, { method: "DELETE" }),

    listRFQCalculatedColumns: (rfqUniquePublicId: string) =>
        req<{ calculatedColumns: RFQCalculatedColumn[] }>(`/v2/rfqs/${rfqUniquePublicId}/calculated-columns`),

    createRFQCalculatedColumn: (
        rfqUniquePublicId: string,
        body: {
            /** Server derives this from `name` when omitted (slugify + auto-suffix on
             *  collision against this RFQ's existing keys). Most callers should leave it
             *  absent; sending it explicitly is supported for backwards compat. */
            key?: string;
            name: string;
            category?: SemanticColumnCategory;
            dataType?: SemanticColumnDataType;
            description: string;
            /** Optional under the classifier-routed pipeline — the classifier infers a
             *  target unit when calculation ends up being the path on a given vendor. Power
             *  users can override here to pin the unit format. */
            targetUnit?: string;
            /** Inputs are now optional and ignored — the planner picks inputs at compute time
             *  from the row's full context. Field retained for backwards compat. */
            inputs?: SemanticColumnRef[];
            llmNotes?: string | null;
            /** RFQ row ids this column should apply to. Empty (or undefined) means every
             *  row. Used by the new-column modal's row picker to scope a recipe to specific
             *  line items when the buyer knows the recipe only makes sense for a subset. */
            applicableRfqRowIds?: number[];
        }
    ) =>
        req<{ calculatedColumn: RFQCalculatedColumn }>(`/v2/rfqs/${rfqUniquePublicId}/calculated-columns`, {
            method: "POST",
            body: JSON.stringify(body)
        }),

    updateRFQCalculatedColumn: (
        rfqUniquePublicId: string,
        id: number,
        body: {
            name?: string;
            description?: string;
            category?: SemanticColumnCategory;
            dataType?: SemanticColumnDataType;
            targetUnit?: string;
            inputs?: SemanticColumnRef[];
            llmNotes?: string | null;
            /** Passing an empty array clears the existing scope (column applies to every
             *  row again). Passing a populated array narrows or changes scope. Omitting the
             *  field entirely leaves the existing scope unchanged. */
            applicableRfqRowIds?: number[];
        }
    ) =>
        req<{ calculatedColumn: RFQCalculatedColumn; recipeVersionBumped: boolean }>(
            `/v2/rfqs/${rfqUniquePublicId}/calculated-columns/${id}`,
            { method: "PATCH", body: JSON.stringify(body) }
        ),

    deleteRFQCalculatedColumn: (rfqUniquePublicId: string, id: number) =>
        req<{ success: true }>(`/v2/rfqs/${rfqUniquePublicId}/calculated-columns/${id}`, { method: "DELETE" }),

    /**
     * Re-run the classifier for one semantic column on this RFQ and apply whatever path it
     * picks (map / derive / calculate / ignore). Differs from a calculate-only retry: the
     * classifier can change its mind between runs, so a column previously routed to
     * "calculate" might land on "map" if the buyer feeds new context or the data shifts.
     *
     * Works for system / team / RFQ-scoped recipes — the QuoteView's `{ scope, key }` is
     * the addressable identifier so the caller doesn't need to look up a recipe row id.
     *
     * `mailboxId` scopes the fan-out to a single vendor mailbox. When omitted, every vendor
     * on the RFQ runs. `feedback` is free-text the buyer can pass through (e.g. "the vendor
     * emailed separately to clarify their UOM"); the classifier reads it as an extra signal.
     *
     * For RFQ-scoped + all-vendors, the server bumps the recipe's `updatedAt` so the
     * view-wide "Processing…" state lights up; per-vendor or team/system calls skip the
     * bump (the per-vendor spinner is the visual).
     */
    reprocessSemanticColumn: (
        rfqUniquePublicId: string,
        params: {
            scope: "system" | "team" | "rfq";
            key: string;
            mailboxId?: number;
            feedback?: string;
            /** RFQ-scoped columns only: optional row-scope update applied alongside the
             *  reprocess. Empty array clears the scope; populated array narrows it; omit
             *  to leave the existing scope unchanged. */
            applicableRfqRowIds?: number[];
        }
    ) =>
        req<{ calculatedColumn?: RFQCalculatedColumn | null; scope: "system" | "team" | "rfq" }>(
            `/v2/rfqs/${rfqUniquePublicId}/calculated-columns/reprocess`,
            { method: "POST", body: JSON.stringify(params) }
        )
};
